import { useFloating } from '@floating-ui/react-dom-interactions';
import TimePickerInput, { TimeValueType } from '@shared/components/DateInput/TimePicker/TimePickerInput';
import { MINUTE_STEP } from '@shared/components/DateInput/utils';
import Input from '@shared/components/InputGroup/Input';
import moment from 'moment';
import { useRef } from 'react';
import { useToggle } from 'rooks';

import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';
import useHideOnOutsideClick from 'hooks/useHideOnOutsideClick';
import testingConstants from 'utils/testingConstants';

import { getDefaultDateInputFloatingProps } from './utils';

interface TimeInputProps {
  value: Date | undefined | null;
  name: string;
  onChange: (newDate: Date) => void;
  onConfirm?: (time?: Date) => void;
  placeholder?: string;
  disabledBeforeTime?: Date;
}

const TimeInput = ({ name, value, onChange, onConfirm, placeholder, disabledBeforeTime }: TimeInputProps) => {
  const [isOpen, toggleIsOpen] = useToggle(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const { x, y, reference, floating, strategy } = useFloating(getDefaultDateInputFloatingProps('bottom'));

  const handleClickOutside = () => {
    toggleIsOpen();
    if (onConfirm) onConfirm();
  };

  useHideOnOutsideClick({ ref: pickerRef, onClickOutside: handleClickOutside, additionalSelector: `#${name}` });

  const handleHourChange = (hour: number) => {
    const newMoment = value ? moment(value).clone().hour(hour) : moment().clone().hour(hour).minute(0);
    onChange(newMoment.toDate());
  };

  const handleMinuteChange = (minute: number) => {
    const newMoment = moment(value ?? undefined)
      .clone()
      .minute(minute);

    const newDate = new Date(newMoment.toDate());

    onChange(newDate);
    if (onConfirm) onConfirm(newDate);
    toggleIsOpen();
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);

  return (
    <div className="w-30 xs:w-36">
      <Input
        name={name}
        id={name}
        inputSize="md"
        className="truncate rounded-lg !bg-gray-10 text-sm font-light cursor-pointer"
        containerClassName="!border-gray-20 focus-within:!border-primary-50 rounded-2xl text-current"
        variant="white-bg"
        autoComplete="off"
        iconClassName="stroke-1 h-5 w-5 stroke-current cursor-pointer"
        isRounded
        onContainerClick={toggleIsOpen}
        readOnly
        icon={ArrowDown}
        value={value ? moment(value).format('HH:mm') : ''}
        placeholder={placeholder}
        ref={reference}
        data-testid={testingConstants.timeInput}
      />
      {isOpen && (
        <div
          className="flex items-center justify-center gap-6 p-4 bg-white rounded-2xl shadow-lg z-10"
          ref={(element) => {
            pickerRef.current = element;
            floating(element);
          }}
          style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
        >
          <TimePickerInput
            values={hours}
            selectedDate={value}
            handleChange={handleHourChange}
            valueType={TimeValueType.HOUR}
            disabledBeforeTime={disabledBeforeTime}
          />
          <TimePickerInput
            values={minutes}
            selectedDate={value}
            handleChange={handleMinuteChange}
            valueType={TimeValueType.MINUTE}
            disabledBeforeTime={disabledBeforeTime}
          />
        </div>
      )}
    </div>
  );
};

export default TimeInput;
